import React from 'react';
import Draggable, {
    type DraggableData,
    type DraggableEvent,
    type DraggableEventHandler,
} from 'react-draggable';
import i18next from 'i18next';

import styles from './StampDialog.module.scss';
import { Button, Checkbox, IconButton, Paper } from '@mui/material';
import { GlobalDialogs } from '../../../pdf/GlobalDialogs';
import { useSelector } from 'react-redux';
import { eplassSliceListStamps, eplassSliceListUserStamps } from '../../../Redux/EplassSlice';
import { GlobalVariables, type StampCategory } from '../../../pdf/GlobalVariables';
import { ReduxUpdater } from '../../../pdf/ReduxUpdater';
import Icon from '@mdi/react';
import { mdiMagnifyMinus, mdiMagnifyPlus, mdiSortAlphabeticalAscending } from '@mdi/js';
import { Close } from '@mui/icons-material';
import type { IStamp, IUserStamp } from '../../../pdf/Interfaces';
import { ApryseSDK } from '../../../pdf/ApryseSDK';
import { generateUniqueKey } from '../../../utils';

type SortOrder = 'Ascending' | 'Descending';

/**
 * Component that shows a configurable menu structure in a Microsoft-Office-like structure.
 */
const StampDialog = () => {
    const nodeRef = React.useRef(null);
    const stamps = useSelector(eplassSliceListStamps);
    const userStamps = useSelector(eplassSliceListUserStamps);

    const [categorySorting, updateCategorySorting] = React.useState<SortOrder>('Ascending');
    const [imageHeight, setImageHeight] = React.useState<number>(30);
    // Random value to guarantee a refresh even if toggleRefreshState gets called multiple times (even ones) in a row
    // A boolean value would not change if toggleRefreshState is called 2, 4, 6, ... times in a row
    const [refreshState, toggleRefreshState] = React.useReducer(() => Math.random(), 0);
    const refreshDialog = React.useCallback(() => toggleRefreshState(), []);

    const onCategoryChecked = (title: string, newChecked: boolean) => {
        if (title === i18next.t('stampList.defaultGroup')) {
            GlobalVariables.isDefaultCategorySelected = newChecked;
        } else {
            GlobalVariables.userCategoriesList[
                GlobalVariables.userCategoriesList.findIndex(ctg => ctg.title === title)
            ].isChecked = newChecked;
        }
        refreshDialog();
    };

    const onCloseForm = (): void => {
        GlobalDialogs.closeStampDialog();
    };

    const changeSelectedStamp = (stampName: string) => {
        ReduxUpdater.selectedStamp = stampName;
        ApryseSDK.setToolMode(ApryseSDK.ToolsENUM.CREATE_STAMP);
        onCloseForm();
    };

    const StampButton = props => (
        <li
            style={{
                backgroundColor: props.selected ? '#0080ff' : 'transparent',
                display: 'inline-block',
                padding: '1px 5px',
            }}
        >
            <Button
                onClick={() => {
                    props.onClick(props.stamp.name);
                }}
            >
                <img
                    alt={props.stamp.name}
                    src={props.stamp.preview}
                    style={{
                        cursor: 'pointer',
                        height: `${props.height ?? 30}px`,
                    }}
                />
            </Button>
        </li>
    );

    const UserStampButton = props => (
        <li
            style={{
                backgroundColor: props.selected ? '#0080ff' : 'transparent',
                display: 'inline-block',
                padding: '1px 5px',
            }}
        >
            <Button
                onClick={() => {
                    props.onClick(props.stamp.id);
                }}
            >
                <img
                    alt={`stampId-${props.stamp.id}`}
                    src={`/api/stamps/image/${props.stamp.id}/64`}
                    style={{
                        cursor: 'pointer',
                        height: `${props.height ?? 30}px`,
                    }}
                    loading='lazy'
                />
            </Button>
        </li>
    );

    const CategoryListItem = props => (
        <li
            key={props.withKey}
            style={{
                display: 'block',
                padding: '0px',
            }}
        >
            <Checkbox
                checked={props.isChecked}
                style={{
                    display: 'inline-flex',
                    marginTop: '1px',
                    verticalAlign: 'middle',
                    padding: '0px 5px 0px 0px',
                }}
                onChange={(_, checked) => props.onChange(props.category, checked ?? false)}
            />
            <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                {props.category}
            </span>
        </li>
    );

    const stampGroup = (title: string, stamps: IStamp[]) => {
        return (
            <>
                <span style={{ backgroundColor: '#ddd', display: 'block', padding: '5px' }}>
                    {title}
                </span>
                <ul
                    style={{
                        listStyleType: 'none',
                        padding: '0px',
                        border: '0px',
                        margin: '5px',
                    }}
                >
                    {stamps.map((stamp, i) => (
                        <StampButton
                            key={generateUniqueKey('stampBtn', i)}
                            selected={false}
                            stamp={stamp}
                            height={imageHeight}
                            onClick={changeSelectedStamp}
                        />
                    ))}
                </ul>
            </>
        );
    };

    const renderStampCategories = React.useMemo((): JSX.Element => {
        const compareFn = (a: StampCategory, b: StampCategory) =>
            a.title === b.title ? 0 : a.title < b.title ? -1 : 1;
        let userCategories: StampCategory[] = [];

        const allCategories = [
            {
                title: i18next.t('stampList.defaultGroup'),
                isChecked: GlobalVariables.isDefaultCategorySelected,
            },
            ...GlobalVariables.userCategoriesList,
        ];

        if (categorySorting === 'Ascending') {
            userCategories = allCategories.sort(compareFn);
        } else {
            userCategories = allCategories.sort(compareFn).reverse();
        }

        return (
            <ul style={{ listStyleType: 'none', padding: '0px', border: '0px', margin: '5px' }}>
                {userCategories.map((ctg, i) => (
                    <CategoryListItem
                        key={generateUniqueKey('CategoryListItem', i)}
                        withKey={`UserCategory-${i}`}
                        category={ctg.title}
                        isChecked={ctg.isChecked}
                        onChange={onCategoryChecked}
                    />
                ))}
            </ul>
        );
    }, [categorySorting, refreshState]);

    const userStampGroup = (key: number, title: string, stamps: IUserStamp[] | undefined) => {
        if (!stamps || stamps.length === 0) {
            return null;
        }
        return (
            <div key={`UserStampGroup-${key}`}>
                <span style={{ backgroundColor: '#ddd', display: 'block', padding: '5px' }}>
                    {title}
                </span>
                <ul
                    style={{
                        listStyleType: 'none',
                        padding: '0px',
                        border: '0px',
                        margin: '5px',
                    }}
                >
                    {stamps.map((stamp, i) => (
                        <UserStampButton
                            key={`UserStampButton-${key}-${i}`}
                            selected={false}
                            stamp={stamp}
                            height={imageHeight}
                            onClick={changeSelectedStamp}
                        />
                    ))}
                </ul>
            </div>
        );
    };

    const onSortButtonClick = (): void => {
        updateCategorySorting(categorySorting === 'Ascending' ? 'Descending' : 'Ascending');
    };

    const renderDefaultStamps = React.useMemo(() => {
        if (!GlobalVariables.isDefaultCategorySelected || !stamps) {
            return null;
        }

        return stampGroup(i18next.t('stampList.defaultGroup'), stamps);
    }, [refreshState, imageHeight]);

    React.useMemo(() => {
        GlobalVariables.loadUserCategories();
        refreshDialog();
    }, [userStamps]);

    const renderUserStamps = React.useMemo(() => {
        if (!GlobalVariables.userCategoriesList || !userStamps) {
            return null;
        }

        const stmps = userStamps;
        const ctgs = GlobalVariables.userCategoriesList.filter(ctg => ctg.isChecked);

        if (ctgs.length == 0 && !GlobalVariables.isDefaultCategorySelected) {
            return stampGroup(i18next.t('stampList.noCategoriesSelected'), []);
        }

        let groups = ctgs.map(ctg => {
            return {
                groupTitle: ctg.title,
                groupItems: stmps.filter(stmp => (stmp.category === ctg.title ? stmp : null)),
            };
        });

        const groupCompareFn = (a, b) =>
            a.groupTitle === b.groupTitle ? 0 : a.groupTitle < b.groupTitle ? -1 : 1;
        groups =
            categorySorting === 'Ascending'
                ? groups.sort(groupCompareFn)
                : groups.sort(groupCompareFn).reverse();

        return groups.map((group, i) => userStampGroup(i, group.groupTitle, group.groupItems));
    }, [refreshState, categorySorting, imageHeight]);

    const handleOnStop: DraggableEventHandler = (e: DraggableEvent, data: DraggableData) => {
        const newPosition = {
            x: data.x,
            y: data.y,
        };
        GlobalVariables.stampDialogPosition = newPosition;
        refreshDialog();
    };

    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
            position={GlobalVariables.stampDialogPosition}
            onStop={handleOnStop}
        >
            <Paper
                ref={nodeRef}
                style={{
                    backgroundColor: 'white',
                    border: '1px inset black',
                    display: 'flex',
                    width: 'min(560px, 100vw - 100px)',
                }}
            >
                <div
                    id="stampList"
                    style={{
                        margin: '10px',
                    }}
                >
                    <h3
                        id="draggable-dialog-title"
                        style={{ cursor: 'move', margin: '0px 0px 5px 0px' }}
                    >
                        {i18next.t('stampList.manageStamps')}
                    </h3>
                    <IconButton
                        onClick={onCloseForm}
                        style={{ position: 'absolute', right: '0px', top: '0px' }}
                    >
                        <Close />
                    </IconButton>
                    <div
                        id="stampCategoryListToolbar"
                        style={{
                            float: 'left',
                            height: '32px',
                            marginBottom: '5px',
                            width: '33%',
                        }}
                    >
                        <IconButton
                            className={styles.menuButton}
                            title={i18next.t('stampList.sortCategories')}
                            onClick={onSortButtonClick}
                        >
                            <Icon path={mdiSortAlphabeticalAscending} />
                        </IconButton>
                    </div>
                    <div
                        id="stampListToolbar"
                        style={{
                            float: 'right',
                            height: '32px',
                            marginBottom: '5px',
                            width: '65%',
                        }}
                    >
                        <IconButton
                            className={styles.menuButton}
                            title={i18next.t('stampList.zoomIn')}
                            disabled={imageHeight >= 95}
                            onClick={_ => setImageHeight(imageHeight + 5)}
                        >
                            <Icon path={mdiMagnifyPlus} />
                        </IconButton>
                        <IconButton
                            className={styles.menuButton}
                            title={i18next.t('stampList.zoomOut')}
                            disabled={imageHeight <= 20}
                            onClick={_ => setImageHeight(imageHeight - 5)}
                        >
                            <Icon path={mdiMagnifyMinus} />
                        </IconButton>
                    </div>
                    <div
                        id="stampCategoryList"
                        style={{
                            backgroundColor: 'whitesmoke',
                            border: '1px solid #aaa',
                            float: 'left',
                            height: '350px',
                            overflowY: 'scroll',
                            width: '32%',
                            marginTop: '5px',
                        }}
                    >
                        {renderStampCategories}
                    </div>
                    <div
                        id="stampList"
                        style={{
                            backgroundColor: 'whitesmoke',
                            border: '1px solid #aaa',
                            float: 'right',
                            height: '350px',
                            overflowY: 'scroll',
                            width: '65%',
                            marginTop: '5px',
                        }}
                    >
                        {renderDefaultStamps}
                        {renderUserStamps}
                    </div>
                </div>
            </Paper>
        </Draggable>
    );
};

export default StampDialog;
